// The rule vocabulary the server serves and the vocabulary refusals it returns
// (agion/Agion#2859; challenge 2026-10-06-a19-registry-rule-builder Q4, Q5).
// The server admits every rule write; this module only reads its answers.
import { isApiFailure } from '@agion/ui/api-failure'
import { unifiedApi } from '@/lib/api-client'
import type { FactRegistry } from '@/types/fact-registry'
import type { RuleVocabularyViolation, RuleVocabularyWarning } from '@/types/atomic-rules'

const text = (value: unknown): value is string => typeof value === 'string'

function isViolation(value: unknown): value is RuleVocabularyViolation {
  const v = value as Partial<RuleVocabularyViolation> | null
  return !!v && typeof v === 'object' && text(v.path) && text(v.code) && text(v.field)
}

function isWarning(value: unknown): value is RuleVocabularyWarning {
  const w = value as Partial<RuleVocabularyWarning> | null
  return !!w && typeof w === 'object' && text(w.path) && text(w.code) && text(w.field) && text(w.message)
}

/** The well-formed warnings of a rule write's success body; none when the key is absent. */
export function ruleWarningsOf(body: unknown): RuleVocabularyWarning[] {
  const warnings = (body as { warnings?: unknown } | null)?.warnings
  return Array.isArray(warnings) ? warnings.filter(isWarning) : []
}

/**
 * The violations of a refused rule write: the `violations` of a 400 body the
 * API client kept on its failure (governance-service
 * handlers/atomic_rules_vocabulary.go). Any other failure has none.
 */
export function ruleViolationsOf(error: unknown): RuleVocabularyViolation[] {
  if (!isApiFailure(error) || error.status !== 400) return []
  const violations = (error.data as { violations?: unknown } | null)?.violations
  return Array.isArray(violations) ? violations.filter(isViolation) : []
}

export const ruleVocabularyService = {
  /** GET /v1/governance/fact-registry: owners, admins and auditors only. */
  async getFactRegistry(signal?: AbortSignal): Promise<FactRegistry> {
    const response = await unifiedApi.get<{ data: FactRegistry }>('governance/fact-registry', undefined, { signal })
    if (!response?.data || !Array.isArray(response.data.entries)) throw new Error('Invalid fact registry response')
    return response.data
  },
}
